import { yupResolver } from "@hookform/resolvers/yup";
import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import useDocusignReturnEffects from "./useDocusignReturnEffects";
import Toast, { ToastType } from "../../../../gds/Toast";
import FormButtons from "../FormButtons/FormButtons";
import ConfidentialityAgreement from "./ConfidentialityAgreement";
import ReviewAndSignDocumentsLink from "./ReviewAndSignDocumentsLink";
import useFormEvents from "./useFormEvents";
import useTitle from "../../../../../hooks/useTitle";
import { GeaoContext } from "../../../GeaoContextProvider";
import GeaoVerticalStepperLayout from "../Layout/GeaoVerticalStepperLayout";
import { validationSchema } from "./validation";
import CheckboxButtonGroup, { LayoutTypes } from "../../../../gds/CheckboxButtonGroup";

export default function ReviewAndSignBase({
    flowType,
    verticalStepperElements,
    currentStepperItem,
    componentName,
    header = "Review, Sign, and Submit Application",
    title = "Review, Sign, and Submit Application",
    showReviewDocuments = true,
    requireFcraSigned = true,
    saveAndExitPath,
    validateOnSaveAndExit = true
}) {
    useTitle(title);

    const { agentInterview } = useContext(GeaoContext);
    const hasSignedFcra = agentInterview?.questionAnswers?.HasSignedFCRA?.answer === "true";

    const { handleSubmit, getValues, trigger, control } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            confidentialityNda: agentInterview?.questionAnswers?.ConfidentialityNDA?.answer
        }
    });

    const { documentsWereSigned, errorSigningDocuments } = useDocusignReturnEffects();

    const { onSubmit, onSaveAndExit, saving } = useFormEvents({
        getValues,
        trigger,
        flowType,
        componentName,
        saveAndExitPath,
        validateOnSaveAndExit,
        documentsWereSigned
    });

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={verticalStepperElements}
            currentStepperItem={currentStepperItem}
            header={header}
        >
            <Toast
                header="Success"
                subtext="Your documents have been signed successfully."
                type={ToastType.Success}
                visible={documentsWereSigned}
            />

            <Toast
                subtext="We have not received your signed documents. Please try again."
                type={ToastType.Informational}
                visible={errorSigningDocuments}
            />

            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                {showReviewDocuments && (
                    <>
                        <p>Your application is ready to review before submission.</p>

                        <ol style={{ marginLeft: "3rem", paddingBottom: "4rem" }}>
                            <li style={{ listStyle: "decimal outside" }}>
                                Review each of your forms by selecting <b>"Review and Sign Forms"</b> below.
                            </li>
                            <li style={{ listStyle: "decimal outside" }}>
                                If the information on each form is correct, select <b>"Sign Here"</b> to add your eSignature.
                                <gds-tooltip>An eSignature, or electronic signature, is an online consent and agreement that has the same legal validity as a written signature. An eSignature enables you to digitally sign documents, making the process easy, efficient, and secure.</gds-tooltip>
                            </li>
                            <li style={{ listStyle: "decimal outside" }}>
                                Once all forms are signed, select <b>"Confirm Signing"</b> to lock your application.
                            </li>
                            <li style={{ listStyle: "decimal outside" }}>
                                Select <b>"Submit"</b> below to complete your application process.
                            </li>
                        </ol>

                        <ReviewAndSignDocumentsLink flowType={flowType} documentsWereSigned={documentsWereSigned} />
                    </>
                )}

                <ConfidentialityAgreement />

                <div style={{ width: '444px' }}>
                    <CheckboxButtonGroup
                        control={control}
                        name="confidentialityNda"
                        layout={LayoutTypes.OneColumn}
                        fullWidth={true}
                        options={[
                            {
                                value: "true",
                                label: (
                                    <span>
                                        I understand and agree to the above statement.<span className='required'>*</span>
                                    </span>
                                )
                            }
                        ]}
                    />
                </div>

                <p>
                    <span className='required'>*</span> Indicates required field.
                </p>

                <FormButtons
                    onSaveAndExit={onSaveAndExit}
                    disabled={(requireFcraSigned && !hasSignedFcra && !documentsWereSigned) || saving}
                    submitButtonText="Submit"
                />
            </form>
        </GeaoVerticalStepperLayout>
    );
}